import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { label: "Explore", hash: "#features" },
  { label: "Achievements", hash: "#achievements" },
  { label: "Screenshots", hash: "#screenshots" },
  { label: "Pricing", hash: "#pricing" },
  { label: "FAQ", hash: "#faq" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (hash) => (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      setOpen(false);
      const target = document.querySelector(hash);
      if (!target) return;
      if (window.__lenis) {
        // Numeric target: Lenis's selector resolution lands ~96px short.
        const top = target.getBoundingClientRect().top + window.scrollY - 90;
        window.__lenis.scrollTo(top);
      } else {
        target.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "border-b border-crave-border bg-crave-bg/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <Logo testId="navbar-logo" />
        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((link) => (
            <Link
              key={link.hash}
              to={`/${link.hash}`}
              onClick={handleAnchor(link.hash)}
              data-testid={`nav-link-${link.hash.slice(1)}`}
              className="text-sm font-medium text-crave-muted transition-colors duration-300 hover:text-crave-cream"
            >
              {link.label}
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/#get-started"
            onClick={handleAnchor("#get-started")}
            data-testid="nav-cta-button"
            className="hidden rounded-full bg-crave-red px-5 py-2.5 text-sm font-semibold text-crave-cream transition-colors duration-300 hover:bg-crave-red-hover sm:block"
          >
            Get Early Access
          </Link>
          <button
            type="button"
            data-testid="nav-mobile-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((o) => !o)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-crave-border text-crave-cream lg:hidden"
          >
            {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-crave-border bg-crave-bg/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((link) => (
                <Link
                  key={link.hash}
                  to={`/${link.hash}`}
                  onClick={handleAnchor(link.hash)}
                  data-testid={`nav-mobile-link-${link.hash.slice(1)}`}
                  className="rounded-xl px-3 py-3 text-base font-medium text-crave-muted transition-colors duration-300 hover:bg-crave-card hover:text-crave-cream"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/#get-started"
                onClick={handleAnchor("#get-started")}
                data-testid="nav-mobile-cta-button"
                className="mt-2 rounded-full bg-crave-red px-5 py-3 text-center text-sm font-semibold text-crave-cream"
              >
                Get Early Access
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
